import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Loader2, FolderOpen } from "lucide-react";
import { MediaPickerModal } from "@/components/MediaLibrary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve any stored path/URL into a src the browser can load
export const resolveImageUrl = (v) => {
  if (!v) return "";
  if (v.startsWith("data:") || v.startsWith("http")) return v;
  if (v.startsWith("/api/")) return `${BACKEND_URL}${v}`;
  return v;
};

export default function ImageUpload({ value, onChange, label = "Image", testId, accept = "image/*", height = "h-40" }) {
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const inputRef = useRef(null);
  const preview = resolveImageUrl(value);

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  return (
    <div className="space-y-2" data-testid={testId || "image-upload"}>
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <ImageIcon className="w-3.5 h-3.5"/><span className="overline">{label}</span>
      </div>
      <input ref={inputRef} type="file" accept={accept} className="hidden"
        onChange={e => doUpload(e.target.files?.[0])} data-testid={`${testId || "image-upload"}-input`}/>
      {preview ? (
        <div className={`relative rounded-2xl overflow-hidden bg-slate-100 group ${height}`}>
          <img src={preview} alt="" className="w-full h-full object-cover"/>
          <button type="button" onClick={() => onChange("")} className="absolute top-2 right-2 p-1.5 rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`${testId || "image-upload"}-remove`}>
            <X className="w-4 h-4"/>
          </button>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
            className="absolute bottom-2 right-2 px-3 py-1.5 rounded-full bg-white/95 text-slate-800 text-xs font-semibold shadow-sm">
            {busy ? "…" : "Replace"}
          </button>
        </div>
      ) : (
        <div className={`w-full ${height} rounded-2xl border-2 border-dashed border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 flex flex-col items-center justify-center text-slate-500 gap-2 transition-colors`}>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="flex flex-col items-center gap-1" data-testid={`${testId || "image-upload"}-picker`}>
            {busy ? <Loader2 className="w-5 h-5 animate-spin"/> : <Upload className="w-5 h-5"/>}
            <span className="text-xs">{busy ? "Uploading…" : "Click to upload"}</span>
            <span className="text-[10px] text-slate-400">JPG · PNG · WEBP · up to 15 MB</span>
          </button>
          <button type="button" onClick={() => setPickerOpen(true)} className="mt-2 flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 font-semibold" data-testid={`${testId || "image-upload"}-lib-btn`}>
            <FolderOpen className="w-3.5 h-3.5"/>Pick from library
          </button>
        </div>
      )}
      {pickerOpen && <MediaPickerModal onPick={(url) => onChange(url)} onClose={() => setPickerOpen(false)}/>}
    </div>
  );
}


// Multi-image upload — comma-separated list of URLs internally
export function GalleryUpload({ value, onChange, label = "Gallery", testId }) {
  const items = Array.isArray(value) ? value : (value || "").split(",").map(s => s.trim()).filter(Boolean);
  const add = (url) => onChange([...items, url]);
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const doUpload = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        items.push(data.url); // mutate local, we call onChange once at end
      }
      onChange([...items]);
      toast.success(`Uploaded ${files.length} image${files.length > 1 ? "s" : ""}`);
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid={testId || "gallery-upload"}>
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <ImageIcon className="w-3.5 h-3.5"/><span className="overline">{label}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {items.map((u, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group">
            <img src={resolveImageUrl(u)} alt="" className="w-full h-full object-cover"/>
            <button type="button" onClick={() => remove(i)} className="absolute top-1 right-1 p-1 rounded-full bg-slate-900/70 text-white opacity-0 group-hover:opacity-100"><X className="w-3 h-3"/></button>
          </div>
        ))}
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="aspect-square rounded-xl border-2 border-dashed border-slate-200 hover:border-sky-400 hover:bg-sky-50/40 flex flex-col items-center justify-center text-slate-500 gap-1 transition-colors"
          data-testid={`${testId || "gallery"}-add`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
          <span className="text-[10px]">Add</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={e => doUpload(e.target.files)}/>
    </div>
  );
}
